import { createSlice } from '@reduxjs/toolkit';
import { adminListen } from '@skynet/data-admin-listen';
import type { JsonFailure } from '@skynet/data-ws-core';
import { registerErrorMessage, isOkCommand, parseResponse } from '@skynet/data-ws-core';
import type {
  JsonAccountDisableSuccess,
  JsonAccountEnableSuccess,
  JsonAccountError,
} from '@skynet/data-shared-accounts';
import { actionOnMessage } from '@skynet/data-ws-mm';
import { hasProperty, isNumber, isString } from '@skynet/type-guards';
import { AccountStatus } from '@skynet/types-account-status';

import { ServersWsCommands } from './types';
import type {
  InitialDataCamelCase,
  JsonInitialData,
  JsonServer,
  JsonServersSuccess,
  ServersState,
} from './types';

registerErrorMessage(ServersWsCommands.AccountDetails, {
  title: 'Account Details Error',
  getDescription: (json: JsonFailure) =>
    `Sorry, the error has occurred on fetching account details. ${json.message}`,
});

export const initialState: ServersState = {
  error: null,

  servers: [],
  isServersLoading: false,
};

const keepServer = (server: JsonServer) => server;

const ACCOUNT_TEXT_FIELDS = ['label', 'message', 'proxy'] as const;

/**
 * The `EnableAccount` ack carries the updated account as a JSON string in
 * `result`. Its fields are merged into the stored account of the same `name`
 * (the new `status` in particular); an empty string carries no update.
 */
function updateAccountEnableStatus(json: JsonAccountEnableSuccess) {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json.result);
  } catch (err: unknown) {
    console.error('Failed to parse response data for account status update request', err);
    return keepServer;
  }

  if (!hasProperty(parsed, 'name') || !isString(parsed.name) || parsed.name === '') {
    console.error('Invalid response structure for account status update');
    return keepServer;
  }

  const { name } = parsed;
  const update: Partial<JsonAccountError> = {};
  if (hasProperty(parsed, 'status') && isNumber(parsed.status)) {
    update.status = parsed.status;
  }
  for (const field of ACCOUNT_TEXT_FIELDS) {
    if (hasProperty(parsed, field) && isString(parsed[field]) && parsed[field] !== '') {
      update[field] = parsed[field];
    }
  }

  return (server: JsonServer) => ({
    ...server,
    accounts: server.accounts.map((account) =>
      account.name === name ? { ...account, ...update } : account,
    ),
  });
}

function updateAccountDisableStatus(json: JsonAccountDisableSuccess) {
  const { id } = json;
  return (server: JsonServer) => ({
    ...server,
    accounts: server.accounts.map((account) =>
      account.name === id ? { ...account, status: AccountStatus.Disabled } : account,
    ),
  });
}

const allowedCommands = Object.values(ServersWsCommands);

function mapInitialDataToCamelCase(initialData: JsonInitialData): InitialDataCamelCase {
  return {
    himikoVersion: initialData.Version,
    name: initialData.Name,
    ipAddress: initialData.IPAddress,
    lastSocketTime: initialData.LastSocketTime,
    lastWebSocketTime: initialData.LastWebSocketTime,
    startSocketTime: initialData.StartSocketTime,
    startWebSocketTime: initialData.StartWebSocketTime,
    himikoDatabase: initialData.HimikoDatabase,
    himikoDatabaseConnected: initialData.HimikoDatabaseConnected,
    proxy: initialData.Proxy,
    loadingSeconds: initialData.LoadingSeconds,
    monitorPing: initialData.MonitorPing,
    himikoPing: initialData.HimikoPing,
    noResponseCount: initialData.NoResponseCount,
    errorsWithChart: initialData.ErrorsWithChart,
    tooMuchRequestsException: initialData.TooMuchRequestsException,
    noSymbolException: initialData.NoSymbolException,
    wrongApiKeyException: initialData.WrongAPIKeyException,
    wrongIpException: initialData.WrongIPException,
  };
}

const serversSlice = createSlice({
  // The reducer stays mounted at the root `admin` key (see `ServersStore`).
  name: 'admin',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(adminListen, (state, action) => {
      if (action.meta.isInitCall) {
        state.isServersLoading = action.payload.section === 'servers';
      }
    });

    builder.addCase(actionOnMessage, (state, action) => {
      const { json, skipProcessing, error } = parseResponse(action.payload, allowedCommands);

      if (skipProcessing || !json) return;

      const { command, okCommand } = json;

      if (error) {
        state.error = error;
        return;
      }

      if (command === ServersWsCommands.Servers) {
        const { statuses }: JsonServersSuccess = json;

        state.isServersLoading = false;
        state.servers = statuses.map((status): JsonServer => ({
          ...status.monitorStatus,
          ...mapInitialDataToCamelCase(status.initialData),
          description: status.description,
          lastUpdateTime: status.lastUpdateTime,
        }));
      }

      if (isOkCommand(command)) {
        switch (okCommand) {
          case ServersWsCommands.EnableAccount:
            state.servers = state.servers.map(updateAccountEnableStatus(json));
            break;
          case ServersWsCommands.DisableAccount:
            state.servers = state.servers.map(updateAccountDisableStatus(json));
            break;
          default:
        }
      }
    });
  },
});

export default serversSlice.reducer;
